import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';

export default function StatusPage() {
  // In a real app, this would fetch from a status API
  const currentStatus = {
    overall: 'operational', // operational, degraded, outage
    lastUpdated: new Date(),
  };

  const services = [
    {
      name: 'Web Application',
      status: 'operational',
      uptime: 99.98,
      description: 'Main application dashboard and features',
    },
    {
      name: 'API Services',
      status: 'operational',
      uptime: 99.95,
      description: 'Backend API and data processing',
    },
    {
      name: 'Database',
      status: 'operational',
      uptime: 99.99,
      description: 'Data storage and retrieval',
    },
    {
      name: 'Authentication',
      status: 'operational',
      uptime: 99.97,
      description: 'Login and session management',
    },
    {
      name: 'Payment Processing',
      status: 'operational',
      uptime: 99.96,
      description: 'Subscription billing via Stripe',
    },
    {
      name: 'PDF Generation',
      status: 'operational',
      uptime: 99.92,
      description: '1099 form generation and download',
    },
  ];

  const incidents = [
    {
      date: 'Dec 28, 2025 - 10:45 AM PST',
      title: 'Resolved: PDF Generation Delays',
      description:
        'PDF generation was experiencing delays of 5-10 seconds. Issue was caused by increased traffic and has been resolved by scaling infrastructure.',
      status: 'resolved',
      duration: '45 minutes',
    },
    {
      date: 'Dec 15, 2025 - 2:30 PM PST',
      title: 'Resolved: Database Maintenance',
      description:
        'Scheduled maintenance window for database optimization. Service was briefly unavailable during this time.',
      status: 'resolved',
      duration: '15 minutes',
    },
  ];

  const upcomingMaintenance = [
    {
      date: 'Jan 15, 2026 - 2:00 AM PST',
      title: 'Scheduled Maintenance',
      description:
        'Routine system maintenance and security updates. The service will be briefly unavailable during this 30-minute window.',
      duration: '30 minutes',
    },
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational':
        return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      case 'degraded':
        return <WarningIcon sx={{ color: 'warning.main' }} />;
      case 'outage':
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      default:
        return <CheckCircleIcon sx={{ color: 'success.main' }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return 'success';
      case 'degraded':
        return 'warning';
      case 'outage':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'operational':
        return 'All Systems Operational';
      case 'degraded':
        return 'Degraded Performance';
      case 'outage':
        return 'Service Outage';
      default:
        return 'Unknown';
    }
  };

  return (
    <Box
      sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 8 }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              background: 'linear-gradient(135deg, #1E23FE 0%, #0D9E6F 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            System Status
          </Typography>
          <Typography variant="h5" sx={{ color: 'text.secondary', mb: 4 }}>
            Real-time status and uptime monitoring
          </Typography>
        </Box>

        {/* Current Status */}
        <Card
          elevation={2}
          sx={{
            mb: 6,
            bgcolor:
              currentStatus.overall === 'operational'
                ? 'success.50'
                : 'warning.50',
            border: '2px solid',
            borderColor:
              currentStatus.overall === 'operational'
                ? 'success.main'
                : 'warning.main',
          }}
        >
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              {getStatusIcon(currentStatus.overall)}
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {getStatusText(currentStatus.overall)}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Last updated: {currentStatus.lastUpdated.toLocaleString()}
            </Typography>
          </CardContent>
        </Card>

        {/* Service Status */}
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
          Service Components
        </Typography>

        <Grid container spacing={3} sx={{ mb: 6 }}>
          {services.map((service, index) => (
            <Grid item xs={12} key={index}>
              <Card elevation={1}>
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {getStatusIcon(service.status)}
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {service.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {service.description}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={service.status.toUpperCase()}
                      color={getStatusColor(service.status) as any}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: 120 }}
                    >
                      Uptime: {service.uptime}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={service.uptime}
                      sx={{
                        flex: 1,
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: 'success.main',
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Upcoming Maintenance */}
        {upcomingMaintenance.length > 0 && (
          <>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
              Upcoming Maintenance
            </Typography>
            {upcomingMaintenance.map((maintenance, index) => (
              <Alert key={index} severity="info" sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  {maintenance.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {maintenance.date} • Duration: {maintenance.duration}
                </Typography>
                <Typography variant="body2">
                  {maintenance.description}
                </Typography>
              </Alert>
            ))}
          </>
        )}

        {/* Recent Incidents */}
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, mt: 6 }}>
          Recent Incidents
        </Typography>

        {incidents.length === 0 ? (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2">
              No incidents reported in the last 30 days. All systems are running
              smoothly! 🎉
            </Typography>
          </Alert>
        ) : (
          incidents.map((incident, index) => (
            <Card key={index} elevation={1} sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <CheckCircleIcon sx={{ color: 'success.main', mt: 0.5 }} />
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {incident.title}
                      </Typography>
                      <Chip label="Resolved" color="success" size="small" />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {incident.date} • Duration: {incident.duration}
                    </Typography>
                    <Typography variant="body2">
                      {incident.description}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))
        )}

        {/* Performance Metrics */}
        <Card elevation={2} sx={{ mt: 6 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
              30-Day Performance
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{ fontWeight: 700, color: 'success.main' }}
                  >
                    99.97%
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Average Uptime
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{ fontWeight: 700, color: 'primary.main' }}
                  >
                    142ms
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Average Response Time
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{ fontWeight: 700, color: 'info.main' }}
                  >
                    2
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Incidents Resolved
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Subscribe to Updates */}
        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'primary.50',
            borderRadius: 3,
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'primary.200',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            Stay Informed
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Subscribe to status updates to receive notifications about incidents
            and scheduled maintenance.
          </Typography>
          <Typography
            component="a"
            href="mailto:status-subscribe@simplypayroll.com"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              display: 'inline-block',
              textDecoration: 'none',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            Subscribe to Updates
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
